import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideEnvironmentInitializer,
} from '@angular/core';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import { GlobalErrorHandler } from '@/app/core/errors/global-error-handler';
import { maintenanceInterceptor } from '@/app/core/platform/maintenance.interceptor';
import { subscriptionInterceptor } from '@/app/core/platform/subscription.interceptor';
import { errorCodeInterceptor } from '@/app/core/errors/error-code.interceptor';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { authInterceptor } from '@/app/core/auth';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { CompanyRouteWatcher } from '@/app/core/company/company-route-watcher.service';
import { DemoLensService } from '@/app/core/company/demo-lens.service';
import { EtiketCeviriKoprusu } from '@/app/domains/portal/core/etiket-ceviri';
import { provideMIcons } from '@/app/core/icons/provider';
import { Directionality } from '@angular/cdk/bidi';
import { AppDirectionality } from '@/app/core/i18n/app-directionality';
import { providePortalTransloco } from '@/app/core/i18n/transloco.config';
import { provideTheming } from '@/app/core/theming';
import { routes } from './app.routes';

// Türkçe locale — DatePipe 'tr' için gerekli
import { registerLocaleData } from '@angular/common';
import localeTr from '@angular/common/locales/tr';
registerLocaleData(localeTr, 'tr');

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideAnimationsAsync(),
    // Portal sözlüğü. KÖKTE olmak ZORUNDA, rota `providers`'ında DEĞİL:
    // TranslocoService `providedIn: 'root'` tanımlı, yani rota seviyesinde
    // sağlansa bile KÖK injector'da oluşturulmaya çalışılır ve orada
    // TRANSLOCO_TRANSPILER'ı bulamaz → NG0201, ardından NG0200 (döngü) ve
    // localeReadyGuard patlayınca PANEL HİÇ AÇILMAZ. (Tam olarak bu oldu.)
    //
    // Kökte olması vitrine yük bindirmez: sözlük dosyası ancak `load()` ya da
    // bir çeviri boru/direktifi çağrıldığında inilir, vitrin ise Transloco
    // kullanmıyor (metinleri CMS'ten geliyor).
    providePortalTransloco(),
    // CDK yönü kendi `Directionality`sinden okur ve onu yalnız açılışta bir kez
    // belirler. Portal dili RTL'ye geçince açılır menüler yanlış tarafa
    // kayıyordu; bu takas CDK'yı yön değişiminden haberdar ediyor.
    { provide: Directionality, useExisting: AppDirectionality },
    // Sıra önemli: authInterceptor token + X-Active-Company-Id ekler,
    // maintenanceInterceptor 401 yenileme akışına karışmadan 503'leri yakalar.
    // URL'ler bu zincir boyunca GÖRELİ kalır ('/api/...'); SSR'da mutlaklaştırma
    // ServerApiBackend'te, yani transfer cache'in ALTINDA yapılır (bkz.
    // core/http/api-origin.ts).
    provideHttpClient(
      withFetch(),
      // subscriptionInterceptor SONDA: 402'yi 401 yenileme akışına ve 503
      // bakım akışına karışmadan yakalar.
      // errorCodeInterceptor: 500 yanıtındaki hata kodunu (ERR-…) bildirimde
      // gösterir — müşteri destek ekibine bu kodu verir.
      withInterceptors([authInterceptor, maintenanceInterceptor, subscriptionInterceptor, errorCodeInterceptor]),
    ),
    // Vitrin (melbarrel.com) sunucuda render edilir; admin/portal/auth
    // app.routes.server.ts'te RenderMode.Client — o ağaçlar için sunucu HTML'i
    // hiç üretilmez, dolayısıyla orada hydration da devreye girmez.
    provideClientHydration(
      withEventReplay(),
      // POST'lar önbelleğe alınmaz: lead formu gibi yan etkili çağrılar
      // sunucudan taşınıp istemcide "zaten yapılmış" sayılmamalı.
      withHttpTransferCacheOptions({ includePostRequests: false }),
    ),
    // anchorScrolling AÇIK olmak ZORUNDA: kapalıyken router her gezinmede
    // sayfayı başa alıyor ve bir çıpaya ('/en/services#marketing-and-seo')
    // gidildiğinde bizim kaydırmamızın ÜSTÜNE yazıyordu — link çalışıyor,
    // sayfa açılıyor, ama en üste zıplıyordu.
    //
    // Router'ın kendi çıpa kaydırması vitrinde tek başına YETMEZ (içerik
    // asenkron gelir, hedef eleman o an DOM'da yoktur) — cms-page içerik
    // yerleştikten sonra kendi kaydırmasını yapar. Buradaki ayar router'ı
    // yolumuzdan çekmek içindir.
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      })
    ),

    // Locale — Canada English (default). i18n eklenince burası TOKEN ile yönetilecek.
    { provide: LOCALE_ID, useValue: 'en-CA' },

    // Yakalanmayan hataları SystemErrorLog'a bildirir → /admin/system-logs.
    { provide: ErrorHandler, useClass: GlobalErrorHandler },

    // Core
    provideMIcons(),
    provideTheming({
      // 'system' seçeneği kullanıcı menüsünden kaldırıldı — varsayılan da açık tema.
      scheme: 'light',
      primary: '#4f46e5',
      error: '#dc2626',
    }),

    // Şirket değişiminde sektörel sayfalardan dashboard'a yönlendir.
    provideEnvironmentInitializer(() => inject(CompanyRouteWatcher)),
    // Deneme merceği: rota değişimini dinleyip merceği kapatır; layout'tan
    // önce kurulmalı ki ilk NavigationEnd kaçmasın.
    provideEnvironmentInitializer(() => inject(DemoLensService)),
    // Sektörel durum etiketlerini (TS içindeki `*-labels.ts` haritaları)
    // çeviriye bağlar. Açılışta kurulmalı: köprü kurulmadan basılan bir
    // etiket, anahtarın kendisini gösterirdi.
    provideEnvironmentInitializer(() => inject(EtiketCeviriKoprusu)),
  ],
};
